/**
 * ClippingPanel.ts
 * -----------------------------------------------------------------------------
 * Bottom-docked "CLIPPING" card: three dual-handle range sliders (X / Y / Z)
 * that crop the model to an axis-aligned box, via six THREE.Plane objects
 * installed on the renderer's GLOBAL clippingPlanes (so every material in the
 * scene — bridge components, FE mesh, grid, axes — is clipped uniformly with
 * no per-material wiring). Mirrors three.js's `webgl_clipping` example, minus
 * the advanced example's stencil-buffer cross-section caps: cut surfaces show
 * whatever is behind them (open/hollow) rather than a solid fill. Most of
 * this app's materials are already `THREE.DoubleSide` (see BridgeScene.ts),
 * so interior back-faces still render instead of leaving a black void.
 */

import * as THREE from 'three';
import type { Viewport3D } from './Viewport3D';

type Axis = 'x' | 'y' | 'z';

interface AxisRow {
  track: HTMLDivElement;
  fill: HTMLDivElement;
  thumbMin: HTMLDivElement;
  thumbMax: HTMLDivElement;
  labelMin: HTMLSpanElement;
  labelMax: HTMLSpanElement;
  boundMin: number;
  boundMax: number;
  valueMin: number;
  valueMax: number;
}

export class ClippingPanel {
  private readonly viewport: Viewport3D;
  private readonly el: HTMLDivElement;
  private readonly rows: Record<Axis, AxisRow>;
  // [xMin, xMax, yMin, yMax, zMin, zMax] — mutated in place each drag rather
  // than reallocated, and handed to the renderer as one persistent array.
  private readonly planes: THREE.Plane[] = [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), 0),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
    new THREE.Plane(new THREE.Vector3(0, 0, 1), 0),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), 0),
  ];
  private visible = false;
  private hasBounds = false;

  /**
   * @param onClose Called when the panel's own close button is clicked
   *   (in addition to hiding itself) — lets the host (the Blazor toolbar's
   *   toggle button) keep its own on/off state in sync when the panel is
   *   dismissed from inside the canvas instead of via that toolbar button.
   */
  constructor(viewport: Viewport3D, private readonly onClose?: () => void) {
    this.viewport = viewport;

    this.el = document.createElement('div');
    this.el.className = 'bridge-clip hidden';

    const tab = document.createElement('div');
    tab.className = 'bridge-clip-tab';
    tab.textContent = 'CLIPPING';
    this.el.appendChild(tab);

    const body = document.createElement('div');
    body.className = 'bridge-clip-body';
    this.el.appendChild(body);

    const rowsEl = document.createElement('div');
    rowsEl.className = 'bridge-clip-rows';
    body.appendChild(rowsEl);

    this.rows = {
      x: this.buildRow('x', rowsEl),
      y: this.buildRow('y', rowsEl),
      z: this.buildRow('z', rowsEl),
    };

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bridge-clip-close';
    closeBtn.title = 'Close';
    closeBtn.setAttribute('aria-label', 'Close section clipping');
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>';
    closeBtn.addEventListener('click', () => {
      this.hide();
      this.onClose?.();
    });
    body.appendChild(closeBtn);

    viewport.container.appendChild(this.el);
  }

  private buildRow(axis: Axis, parent: HTMLDivElement): AxisRow {
    const row = document.createElement('div');
    row.className = 'bridge-clip-row';

    const label = document.createElement('span');
    label.className = 'bridge-clip-axis';
    label.textContent = axis.toUpperCase();

    const labelMin = document.createElement('span');
    labelMin.className = 'bridge-clip-value bridge-clip-value-min';

    const track = document.createElement('div');
    track.className = 'bridge-clip-track';
    // Full-width rail underneath the fill, so the un-selected portion of the
    // range reads as a track instead of empty space (see the fill, which
    // paints over it between the two handles).
    const rail = document.createElement('div');
    rail.className = 'bridge-clip-rail';
    const fill = document.createElement('div');
    fill.className = 'bridge-clip-fill';
    const thumbMin = document.createElement('div');
    thumbMin.className = 'bridge-clip-thumb';
    thumbMin.tabIndex = 0;
    thumbMin.setAttribute('role', 'slider');
    thumbMin.setAttribute('aria-label', `${axis.toUpperCase()} min`);
    const thumbMax = document.createElement('div');
    thumbMax.className = 'bridge-clip-thumb';
    thumbMax.tabIndex = 0;
    thumbMax.setAttribute('role', 'slider');
    thumbMax.setAttribute('aria-label', `${axis.toUpperCase()} max`);
    track.appendChild(rail);
    track.appendChild(fill);
    track.appendChild(thumbMin);
    track.appendChild(thumbMax);

    const labelMax = document.createElement('span');
    labelMax.className = 'bridge-clip-value bridge-clip-value-max';

    row.appendChild(label);
    row.appendChild(labelMin);
    row.appendChild(track);
    row.appendChild(labelMax);
    parent.appendChild(row);

    const state: AxisRow = {
      track, fill, thumbMin, thumbMax, labelMin, labelMax,
      boundMin: 0, boundMax: 1, valueMin: 0, valueMax: 1,
    };
    this.wireThumb(state, thumbMin, true);
    this.wireThumb(state, thumbMax, false);
    return state;
  }

  private wireThumb(state: AxisRow, thumb: HTMLDivElement, isMin: boolean): void {
    thumb.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      thumb.setPointerCapture(ev.pointerId);
      this.dragTo(state, isMin, ev.clientX);
      const move = (mv: PointerEvent): void => this.dragTo(state, isMin, mv.clientX);
      const up = (): void => {
        thumb.removeEventListener('pointermove', move);
        thumb.removeEventListener('pointerup', up);
      };
      thumb.addEventListener('pointermove', move);
      thumb.addEventListener('pointerup', up);
    });

    thumb.addEventListener('keydown', (ev) => {
      const span = state.boundMax - state.boundMin;
      if (span <= 0) return;
      const step = span / 100;
      let delta = 0;
      if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') delta = -step;
      else if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') delta = step;
      else return;
      ev.preventDefault();
      this.setValue(state, isMin, (isMin ? state.valueMin : state.valueMax) + delta);
    });
  }

  private dragTo(state: AxisRow, isMin: boolean, clientX: number): void {
    const rect = state.track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    this.setValue(state, isMin, state.boundMin + frac * (state.boundMax - state.boundMin));
  }

  private setValue(state: AxisRow, isMin: boolean, raw: number): void {
    const clamped = Math.min(state.boundMax, Math.max(state.boundMin, raw));
    // The two handles can't cross — dragging one past the other pushes it along.
    if (isMin) state.valueMin = Math.min(clamped, state.valueMax);
    else state.valueMax = Math.max(clamped, state.valueMin);
    this.renderRow(state);
    this.applyPlanes();
  }

  private renderRow(state: AxisRow): void {
    const span = state.boundMax - state.boundMin;
    const fracMin = span > 0 ? (state.valueMin - state.boundMin) / span : 0;
    const fracMax = span > 0 ? (state.valueMax - state.boundMin) / span : 1;
    state.thumbMin.style.left = `${fracMin * 100}%`;
    state.thumbMax.style.left = `${fracMax * 100}%`;
    state.fill.style.left = `${fracMin * 100}%`;
    state.fill.style.width = `${Math.max(0, fracMax - fracMin) * 100}%`;
    state.labelMin.textContent = formatValue(state.valueMin);
    state.labelMax.textContent = formatValue(state.valueMax);
    state.thumbMin.setAttribute('aria-valuemin', String(state.boundMin));
    state.thumbMin.setAttribute('aria-valuemax', String(state.boundMax));
    state.thumbMin.setAttribute('aria-valuenow', String(state.valueMin));
    state.thumbMax.setAttribute('aria-valuemin', String(state.boundMin));
    state.thumbMax.setAttribute('aria-valuemax', String(state.boundMax));
    state.thumbMax.setAttribute('aria-valuenow', String(state.valueMax));
  }

  private applyPlanes(): void {
    const { x, y, z } = this.rows;
    // plane.distanceToPoint = normal·point + constant ; kept where this is >= 0.
    this.planes[0].constant = -x.valueMin; // keep x >= valueMin
    this.planes[1].constant = x.valueMax;  // keep x <= valueMax
    this.planes[2].constant = -y.valueMin;
    this.planes[3].constant = y.valueMax;
    this.planes[4].constant = -z.valueMin;
    this.planes[5].constant = z.valueMax;
    if (this.visible) {
      this.viewport.renderer.clippingPlanes = this.planes;
      // Dragging a handle doesn't move the camera, so the on-demand render
      // loop wouldn't otherwise notice the crop changed.
      this.viewport.invalidate();
    }
  }

  /** (Re)initialise all three axis ranges from a fresh bounding box, resetting
   *  every slider to its full (no-clipping) extent. Safe to call whether or
   *  not the panel is currently shown. */
  public setBounds(box: THREE.Box3): void {
    if (box.isEmpty()) return;
    this.setAxisBounds('x', box.min.x, box.max.x);
    this.setAxisBounds('y', box.min.y, box.max.y);
    this.setAxisBounds('z', box.min.z, box.max.z);
    this.hasBounds = true;
    this.applyPlanes();
  }

  private setAxisBounds(axis: Axis, min: number, max: number): void {
    const state = this.rows[axis];
    // Guard a zero-thickness axis so the slider still has a usable span.
    state.boundMin = min;
    state.boundMax = max > min ? max : min + 1;
    state.valueMin = state.boundMin;
    state.valueMax = state.boundMax;
    this.renderRow(state);
  }

  /** Show the panel, (re)framing its sliders on `box` (falls back to whatever
   *  range was already set if `box` is null/empty — e.g. no model loaded). */
  public show(box: THREE.Box3 | null): void {
    if (box && !box.isEmpty()) this.setBounds(box);
    this.visible = true;
    this.el.classList.remove('hidden');
    this.viewport.renderer.clippingPlanes = this.hasBounds ? this.planes : [];
    this.viewport.invalidate();
  }

  public hide(): void {
    this.visible = false;
    this.el.classList.add('hidden');
    this.viewport.renderer.clippingPlanes = [];
    this.viewport.invalidate();
  }

  public get isVisible(): boolean {
    return this.visible;
  }

  /** Current on-screen height (0 while hidden) — lets other bottom-docked
   *  overlays (e.g. the Results contour legend) stack above this one instead
   *  of overlapping it. */
  public get outerHeight(): number {
    return this.visible ? this.el.getBoundingClientRect().height : 0;
  }

  public dispose(): void {
    this.hide();
    if (this.el.parentElement) this.el.parentElement.removeChild(this.el);
  }
}

function formatValue(v: number): string {
  const rounded = Math.round(v * 100) / 100;
  return rounded.toString();
}
